import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { Project, ProjectStatus } from '@/types';

export interface ProjectsState {
  items: Project[];
  selectedProject: Project | null;
  isLoading: boolean;
  error: string | null;
}

const initialState: ProjectsState = {
  items: [],
  selectedProject: null,
  isLoading: false,
  error: null,
};

const projectsSlice = createSlice({
  name: 'projects',
  initialState,
  reducers: {
    setProjects: (state, action: PayloadAction<Project[]>) => {
      state.items = action.payload;
      state.isLoading = false;
    },
    addProject: (state, action: PayloadAction<Project>) => {
      state.items.unshift(action.payload);
    },
    updateProject: (state, action: PayloadAction<Project>) => {
      const index = state.items.findIndex((p) => p.id === action.payload.id);
      if (index !== -1) {
        state.items[index] = action.payload;
      }
      if (state.selectedProject?.id === action.payload.id) {
        state.selectedProject = action.payload;
      }
    },
    updateProjectProgress: (
      state,
      action: PayloadAction<{ projectId: string; progress: number }>
    ) => {
      const project = state.items.find((p) => p.id === action.payload.projectId);
      if (project) {
        project.completionPercentage = action.payload.progress;
      }
    },
    removeProject: (state, action: PayloadAction<string>) => {
      state.items = state.items.filter((p) => p.id !== action.payload);
      if (state.selectedProject?.id === action.payload) {
        state.selectedProject = null;
      }
    },
    setSelectedProject: (state, action: PayloadAction<Project | null>) => {
      state.selectedProject = action.payload;
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
      state.isLoading = false;
    },
  },
});

export const {
  setProjects,
  addProject,
  updateProject,
  updateProjectProgress,
  removeProject,
  setSelectedProject,
  setLoading,
  setError,
} = projectsSlice.actions;

// Selectors
export const selectAllProjects = (state: { projects: ProjectsState }) =>
  state.projects.items;
export const selectSelectedProject = (state: { projects: ProjectsState }) =>
  state.projects.selectedProject;
export const selectProjectsLoading = (state: { projects: ProjectsState }) =>
  state.projects.isLoading;
export const selectProjectById =
  (projectId: string) => (state: { projects: ProjectsState }) =>
    state.projects.items.find((p) => p.id === projectId);

export const selectActiveProjects = (state: { projects: ProjectsState }) =>
  state.projects.items.filter((p) => p.status === ProjectStatus.ACTIVE);

export const selectProjectsByStatus =
  (status: ProjectStatus) => (state: { projects: ProjectsState }) =>
    state.projects.items.filter((p) => p.status === status);

export default projectsSlice.reducer;
